import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
  createFilterOptions
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { listAllInstrumentSummaries } from "./catalogAPI";
import { compactFieldSx } from "./formStyles";

export type InstrumentSuggestion = {
  id: string;
  title: string;
  description: string | null;
};

type Props = {
  value: string;
  sourceInstrumentId?: string;
  required?: boolean;
  /** Disable the field (e.g. when no network is selected upstream). */
  disabled?: boolean;
  /**
   * Called whenever the title text changes. The callback receives the new
   * title AND the instrument suggestion if the typed/selected title exactly
   * matches an existing instrument (so the parent can transition into reuse
   * mode). When no exact match, `match` is null.
   */
  onChange: (next: { title: string; match: InstrumentSuggestion | null }) => void;
  sx?: object;
};

// Module-scoped cache so flipping between instruments / network re-selection
// doesn't re-fetch the full instrument list every time.
let cachedInstruments: InstrumentSuggestion[] | null = null;
let inflight: Promise<InstrumentSuggestion[]> | null = null;

async function loadInstruments(): Promise<InstrumentSuggestion[]> {
  if (cachedInstruments) return cachedInstruments;
  if (!inflight) {
    inflight = listAllInstrumentSummaries()
      .then((res) => {
        cachedInstruments = (res.instruments ?? []).map((i) => ({
          id: i.id,
          title: i.title,
          description: i.description ?? null
        }));
        return cachedInstruments;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

const filterOptions = createFilterOptions<InstrumentSuggestion>({
  matchFrom: "any",
  stringify: (opt) => `${opt.title} ${opt.description ?? ""}`
});

export function InstrumentTitleAutocomplete({
  value,
  sourceInstrumentId: _sourceInstrumentId,
  required,
  disabled,
  onChange,
  sx
}: Props) {
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState<InstrumentSuggestion[]>(
    () => cachedInstruments ?? []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the controlled input in sync if the parent changes the title from
  // somewhere other than this component (e.g., draft reset, instrument switch).
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Load all instruments once on mount (or first time the field is enabled).
  // The dropdown then filters client-side — no debounce, no min-char gate.
  useEffect(() => {
    if (disabled) return;
    if (cachedInstruments) {
      setOptions(cachedInstruments);
      return;
    }
    let cancelled = false;
    setLoading(true);
    loadInstruments()
      .then((list) => {
        if (cancelled) return;
        setOptions(list);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load instruments.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [disabled]);

  // Detect exact match (case-insensitive trim) against the full option list.
  const exactMatch = useMemo<InstrumentSuggestion | null>(() => {
    const trimmedLower = inputValue.trim().toLowerCase();
    if (!trimmedLower) return null;
    return (
      options.find((o) => o.title.trim().toLowerCase() === trimmedLower) ?? null
    );
  }, [inputValue, options]);

  useEffect(() => {
    onChange({ title: inputValue, match: exactMatch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, exactMatch?.id]);

  return (
    <Box sx={sx}>
      <Autocomplete
        size="small"
        freeSolo
        clearOnBlur={false}
        selectOnFocus
        disabled={disabled}
        options={options}
        loading={loading}
        filterOptions={filterOptions}
        getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.title)}
        isOptionEqualToValue={(a, b) =>
          (typeof a === "string" ? a : a.id) === (typeof b === "string" ? b : b.id)
        }
        inputValue={inputValue}
        onInputChange={(_, val) => setInputValue(val)}
        onChange={(_, val) => {
          if (val && typeof val === "object") {
            setInputValue(val.title);
          }
        }}
        sx={compactFieldSx}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Instrument title"
            required={required}
            placeholder={
              disabled ? "Pick a target network first" : "Search existing or type new"
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={14} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        renderOption={(props, opt) => (
          <Box component="li" {...props} key={opt.id}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {opt.title}
              </Typography>
              {opt.description ? (
                <Typography variant="caption" color="text.secondary">
                  {opt.description}
                </Typography>
              ) : null}
            </Box>
          </Box>
        )}
      />
      {error ? (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}
